import type { ReactNode } from "react";
import { moduleCopy, uiCopy } from "./signup-copy";
import type { Language, ModuleKey } from "./signup-copy";

export function SignupEcosystem({
  language,
  activeModule,
  onSelect
}: {
  language: Language;
  activeModule: ModuleKey;
  onSelect: (key: ModuleKey) => void;
}) {
  const text = uiCopy[language];
  const activeCopy = moduleCopy[language][activeModule];
  return (
    <>
      <div className="ss-eco" aria-label={language === "ar" ? "منظومة Leylion" : "Leylion ecosystem"}>
        <div className="ss-orbit ss-o1" /><div className="ss-orbit ss-o2" /><div className="ss-orbit ss-o3" />
        {Array.from({ length: 8 }, (_, index) => <div className={`ss-route ss-r${index + 1}`} key={`route-${index}`} />)}

        <ModuleNode moduleKey="sales" className="ss-sales" language={language} active={activeModule === "sales"} onSelect={onSelect}><SalesIcon /></ModuleNode>
        <ModuleNode moduleKey="inventory" className="ss-inventory" language={language} active={activeModule === "inventory"} onSelect={onSelect}><InventoryIcon /></ModuleNode>
        <ModuleNode moduleKey="branches" className="ss-branches" language={language} active={activeModule === "branches"} onSelect={onSelect}><BranchesIcon /></ModuleNode>
        <ModuleNode moduleKey="customers" className="ss-customers" language={language} active={activeModule === "customers"} onSelect={onSelect}><CustomersIcon /></ModuleNode>
        <ModuleNode moduleKey="reports" className="ss-reports" language={language} active={activeModule === "reports"} onSelect={onSelect}><ReportsIcon /></ModuleNode>
        <ModuleNode moduleKey="team" className="ss-team" language={language} active={activeModule === "team"} onSelect={onSelect}><TeamIcon /></ModuleNode>
        <ModuleNode moduleKey="operations" className="ss-operations" language={language} active={activeModule === "operations"} onSelect={onSelect}><OperationsIcon /></ModuleNode>
        <ModuleNode moduleKey="devices" className="ss-devices" language={language} active={activeModule === "devices"} onSelect={onSelect}><DevicesIcon /></ModuleNode>

        <article className="ss-core">
          <div className="ss-core-top">
            <div className="ss-core-brand"><b>L</b><span>Leylion</span></div>
            <div className="ss-connected"><i /><span>{text.connected}</span></div>
          </div>
          <div className="ss-core-content">
            <div className="ss-kicker">{activeCopy.kicker}</div>
            <div className="ss-core-title">{activeCopy.headline}</div>
            <div className="ss-core-copy">{activeCopy.body}</div>
            <div className="ss-flow">
              <span className="ss-pill">{activeCopy.flow[0]}</span><span className="ss-arrow">→</span>
              <span className="ss-pill">{activeCopy.flow[1]}</span><span className="ss-arrow">→</span>
              <span className="ss-pill">{activeCopy.flow[2]}</span>
            </div>
          </div>
        </article>
      </div>

      <div className="ss-trust">
        <span><i>✓</i>{text.trustOne}</span>
        <span><i>✓</i>{text.trustTwo}</span>
        <span><i>✓</i>{text.trustThree}</span>
      </div>
    </>
  );
}

function ModuleNode({
  moduleKey,
  className,
  language,
  active,
  onSelect,
  children
}: {
  moduleKey: ModuleKey;
  className: string;
  language: Language;
  active: boolean;
  onSelect: (key: ModuleKey) => void;
  children: ReactNode;
}) {
  const item = moduleCopy[language][moduleKey];
  return (
    <button className={`ss-node ${className}${active ? " is-active" : ""}`} type="button" onClick={() => onSelect(moduleKey)}>
      <span className="ss-icon">{children}</span>
      <span><strong>{item.title}</strong><small>{item.sub}</small></span>
    </button>
  );
}

function SalesIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect className="soft" x="4" y="5" width="16" height="14" rx="4" /><path d="M5 18h14M7.5 15v-3M12 15V9M16.5 15v-5M7 9.5l3-3 2.5 2.5L17 4.5M14.5 4.5H17v2.5" /></svg>; }
function InventoryIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path className="soft" d="M12 3.8 4.7 7.6v8.8L12 20.2l7.3-3.8V7.6L12 3.8Z" /><path d="M12 3.8 4.7 7.6 12 11.4l7.3-3.8L12 3.8ZM4.7 7.6v8.8L12 20.2l7.3-3.8V7.6M12 11.4v8.8" /></svg>; }
function BranchesIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect className="soft" x="3.5" y="4.5" width="5.5" height="4.5" rx="1.4" /><rect className="soft" x="14.5" y="4.5" width="5.5" height="4.5" rx="1.4" /><rect className="soft" x="9.25" y="14.5" width="5.5" height="4.5" rx="1.4" /><rect x="3.5" y="4.5" width="5.5" height="4.5" rx="1.4" /><rect x="14.5" y="4.5" width="5.5" height="4.5" rx="1.4" /><rect x="9.25" y="14.5" width="5.5" height="4.5" rx="1.4" /><path d="M6.25 9v2.5h11.05V9M12 11.5v3" /></svg>; }
function CustomersIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><circle className="soft" cx="12" cy="12" r="8" /><circle cx="10" cy="9" r="2.6" /><path d="M5.5 18a4.9 4.9 0 0 1 9 0M16 10.2a2.2 2.2 0 0 1 2.4 2.2M17 18a4 4 0 0 0-2.5-3.4" /></svg>; }
function ReportsIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path className="soft" d="M6 18V11.8a1.8 1.8 0 0 1 1.8-1.8H18v8Z" /><path d="M5 18h14M8 18v-4.5M12 18V8M16 18v-6M7 9.5 11 6l2.5 2L17.5 4" /></svg>; }
function TeamIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path className="soft" d="M12 4.5 18.5 7v4.6c0 3.3-2.2 6.3-6.5 8-4.3-1.7-6.5-4.7-6.5-8V7Z" /><circle cx="12" cy="10" r="2.4" /><path d="M8.5 16a4.2 4.2 0 0 1 7 0M12 4.5 18.5 7v4.6c0 3.3-2.2 6.3-6.5 8-4.3-1.7-6.5-4.7-6.5-8V7Z" /></svg>; }
function OperationsIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="7.1" style={{ strokeWidth: .78, opacity: .82 }} /><circle cx="12" cy="4.9" r="1.05" className="ss-solid" /><circle cx="17.55" cy="7.58" r="1.05" className="ss-solid" /><circle cx="18.92" cy="13.58" r="1.05" className="ss-solid" /><circle cx="15.08" cy="18.4" r="1.05" className="ss-solid" /><circle cx="8.92" cy="18.4" r="1.05" className="ss-solid" /><circle cx="5.08" cy="13.58" r="1.05" className="ss-solid" /><circle cx="6.45" cy="7.58" r="1.05" className="ss-solid" /><circle cx="12" cy="12" r="1.05" className="ss-solid" /></svg>; }
function DevicesIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect className="soft" x="3.5" y="5" width="11" height="8" rx="2" /><rect className="soft" x="16" y="7" width="4.5" height="10" rx="1.7" /><rect x="3.5" y="5" width="11" height="8" rx="2" /><path d="M8 16.8h2M6.5 13.1v1.7h5" /><rect x="16" y="7" width="4.5" height="10" rx="1.7" /><path d="M17.5 10.2h1.4M17.5 13h1.4" /></svg>; }
